import React, { useState, useEffect, useRef } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { IMAGES } from "../constants/image";
import { useLocation } from "react-router-dom";
const faqs = [
  {
    question: "What is PBCCR?",
    answer:
      "PBCCR is a population-based childhood cancer registry that records childhood cancer cases within a defined geographic area to support surveillance, research, planning, and better care.",
  },
  {
    question: "Who can use this platform?",
    answer:
      "The platform is intended for authorized healthcare professionals, registry staff, researchers, administrators, and approved stakeholders who work with childhood cancer registry information.",
  },
  {
    question: "Is the data reliable?",
    answer:
      "PBCCR data is collected through structured registry methods, multiple source verification, validation checks, and secure data handling practices to support reliable reporting.",
  },
  {
    question: "Where can I understand the calculation methods?",
    answer:
      "Visit the Methods page to review incidence definitions, population methods, formulas, and the approach used for registry analysis.",
  },
  {
    question: "Who should I contact for PBCCR support?",
    answer:
      "Contact the Population-Based Childhood Cancer Registry team at Cancer Institute (WIA), Chennai, or email pbccr@cancerinstitutewia.org for registry-related support.",
  },
];

const Help = () => {
  const [activeFaq, setActiveFaq] = useState(faqs[0].question);
  const [activeTab, setActiveTab] = useState("a");
  const contentRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const toggleFaq = (question: string) => {
    setActiveFaq((currentQuestion) =>
      currentQuestion === question ? "" : question,
    );
  };
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [activeTab, location.pathname]);

  const ICON_MAP: Record<string, string> = {
    "bagchi-Logo": IMAGES.HEADER.BAGCHI_LOGO,
    "search-icon": IMAGES.HEADER.SEARCH_ICON,
    "user-icon": IMAGES.PROFILE.USER_PROFILE,
    "getting-started-img1": IMAGES.HELP.GETTING_STARTED_IMG1,
    "getting-started-img2": IMAGES.HELP.GETTING_STARTED_IMG2,
    "getting-started-img3": IMAGES.HELP.GETTING_STARTED_IMG3,
    "signup-img1": IMAGES.HELP.SIGNUP_IMG1,
    "signup-img2": IMAGES.HELP.SIGNUP_IMG2,
    "signup-img3": IMAGES.HELP.SIGNUP_IMG3,
    "signup-img4": IMAGES.HELP.SIGNUP_IMG4,
    "signup-img5": IMAGES.HELP.SIGNUP_IMG5,
    "signup-img6": IMAGES.HELP.SIGNUP_IMG6,
    "login-img1": IMAGES.HELP.LOGIN_IMG1,
    "login-img2": IMAGES.HELP.LOGIN_IMG2,
    "login-img3": IMAGES.HELP.LOGIN_IMG3,
    "change-password-img1": IMAGES.HELP.CHANGE_PASSWORD_IMG1,
    "change-password-img2": IMAGES.HELP.CHANGE_PASSWORD_IMG2,
    "change-password-img3": IMAGES.HELP.CHANGE_PASSWORD_IMG3,
    "forgot-password-img1": IMAGES.HELP.FORGOT_PASSWORD_IMG1,
    "forgot-password-img2": IMAGES.HELP.FORGOT_PASSWORD_IMG2,
    "forgot-password-img3": IMAGES.HELP.FORGOT_PASSWORD_IMG3,
    "forgot-password-img4": IMAGES.HELP.FORGOT_PASSWORD_IMG4,
    "forgot-password-img5": IMAGES.HELP.FORGOT_PASSWORD_IMG5,
    "profile-img1": IMAGES.HELP.PROFILE_IMG1,
    "profile-img2": IMAGES.HELP.PROFILE_IMG2,
    "logout-img1": IMAGES.HELP.LOGOUT_IMG1,
    "logout-img2": IMAGES.HELP.LOGOUT_IMG2,
    "homepage-img1": IMAGES.HELP.HOME_PAGE_IMG1,
    "homepage-img2": IMAGES.HELP.HOME_PAGE_IMG2,
    "homepage-img3": IMAGES.HELP.HOME_PAGE_IMG3,
    "homepage-img4": IMAGES.HELP.HOME_PAGE_IMG4,
    "factsheet-img1": IMAGES.HELP.FACTSHEET_IMG1,
    "factsheet-img2": IMAGES.HELP.FACTSHEET_IMG2,
    "data-viz-img1": IMAGES.HELP.DATA_VIZ_IMG1,
    "data-viz-img2": IMAGES.HELP.DATA_VIZ_IMG2,
    "data-viz-img3": IMAGES.HELP.DATA_VIZ_IMG3,
    "data-viz-img4": IMAGES.HELP.DATA_VIZ_IMG4,
    "footer-img1": IMAGES.HELP.FOOTER_IMG1,
    // getting_start_vid: IMAGES.HELP.GETTING_START,
    // global_footer_vid: IMAGES.HELP.GLOBAL_FOOTER,
    // home: IMAGES.HELP.HOME,
    // login: IMAGES.HELP.LOGIN,
    // logout: IMAGES.HELP.LOGOUT,
    // methods_initiatives: IMAGES.HELP.METHODS_INITIATIVES,
    // profile: IMAGES.HELP.PROFILE,
    // signup: IMAGES.HELP.SIGNUP,
    // about_help: IMAGES.HELP.ABOUT_HELP,
    // forgot_password: IMAGES.HELP.FORGOT_PASSWORD,
    // change_password: IMAGES.HELP.CHANGE_PASSWORD,
    // data_visualization: IMAGES.HELP.DATA_VISUALIZATION,
    // factsheet: IMAGES.HELP.FACTSHEET,
  };
  const renderContent = () => {
    switch (activeTab) {
      case "a":
        return (
          <>
            <h5>Introduction and Overview</h5>
            <p>
              The TN-PBCCR Visualization Portal is a web-based analytical
              platform developed to present structured childhood cancer
              incidence and mortality statistics for Tamil Nadu. The portal
              transforms registry data into interactive visualizations and
              analytical dashboards that support epidemiological assessment,
              district-level monitoring, public health planning, and
              evidence-based decision making.
            </p>
            <p>
              The data architecture and statistical indicators used within the
              portal are aligned with established cancer registry analytical
              frameworks and standardized epidemiological methodologies. The
              platform facilitates data-driven interpretation of childhood
              cancer patterns and enables regional epidemiological evaluation
              across districts of Tamil Nadu.
            </p>
            <br></br>
            {/* ---------------- */}
            <h5>Core Objectives of TN-PBCCR</h5>

            <h6>Monitor Childhood Cancer Incidence</h6>
            <p>
              To monitor and analyze childhood cancer incidence patterns across
              different populations, geographic regions, and time periods in
              order to identify trends and emerging disease burdens.
            </p>
            <h6>Support Epidemiological Research</h6>
            <p>
              To provide researchers, clinicians, and public health
              professionals with structured and reliable datasets for studying
              childhood cancer distribution, determinants, survival, and
              prevention strategies.
            </p>
            <h6>Enable Public Health Planning</h6>
            <p>
              To support healthcare planning, policy formulation, and resource
              allocation through evidence-based childhood cancer statistics,
              projections, and regional burden estimates.
            </p>
            <br></br>
            {/* ---------------- */}
            <h5>Purpose of the Visualization Portal</h5>
            <h6>Data Visualization and Interpretation</h6>
            <p>
              To convert complex childhood cancer registry data into clear,
              interactive, and user-friendly visual analytics for easier
              interpretation and dissemination.
            </p>
            <h6>District-Level Analysis</h6>
            <p>
              To facilitate detailed examination of regional childhood cancer
              patterns and variations across districts of Tamil Nadu.
            </p>
            <h6>Trend Monitoring</h6>
            <p>
              To monitor temporal changes in childhood cancer incidence,
              distribution, and related outcomes over time.
            </p>
            <h6>Policy and Research Support</h6>
            <p>
              To provide scientific evidence and statistical insights that
              support healthcare planning, cancer control activities, academic
              research, and policy decision making.
            </p>
          </>
        );
      case "b":
        return (
          <>
            <h5>System Requirements</h5>
            <p>
              To ensure optimal performance and compatibility, please ensure
              that your system meets the following recommendations.
            </p>
            <br></br>
            <h6>Web Browser</h6>
            <p>
              Latest versions of Google Chrome, Microsoft Edge, or Mozilla
              Firefox are recommended for optimal performance and compatibility
            </p>
            <h6>Memory (RAM)</h6>
            <p>
              Minimum 8 GB RAM recommended to support large datasets and
              interactive visualizations smoothly.
            </p>
            <h6>Screen Resolution</h6>
            <p>
              Minimum resolution of 1366 × 768 for proper display of charts and
              data tables.
            </p>
            <h6>Internet Connection</h6>
            <p>
              A stable broadband internet connection is required for data
              loading and real time analytics.
            </p>
          </>
        );
      case "c":
        return (
          <>
            <h5>User Access Levels</h5>
            <p>
              The portal operates with two primary access tiers based on user
              verification.
            </p>
            <h6>Public Users</h6>
            <p>
              Guest access allows viewing of Fact Sheets, Methods, and Recent
              Cancer Statistics. The following applies to public users:
            </p>
            <ul className="circle-list">
              <li>Fact sheets are available for download.</li>
              <li>Data visualization features are limited to viewing only</li>
              <li>
                No registration or login is required for public content access
              </li>
            </ul>
            <h6>Authenticated Users</h6>
            <p>
              Users with verified credentials are granted full analytical
              capabilities, including:
            </p>
            <ul className="circle-list">
              <li>Advanced filtering and interactive data exploration.</li>
              <li>Comprehensive data export options.</li>
              <li> Access to complete registry datasets and dashboards</li>
            </ul>
          </>
        );
      case "d":
        return (
          <>
            <h5>Getting Started</h5>
            <p>
              This section provides an overview of how to begin using the
              TN-PBCCR Visualization Portal.
            </p>
            <h6>Accessing the Portal</h6>
            <strong>Step1:</strong>
            <p>
              Open a web browser such as Google Chrome, Microsoft Edge, or
              Mozilla Firefox on your computer or mobile device
            </p>
            <img
              src={ICON_MAP["getting-started-img1"]}
              alt="getting-started-img1"
            />
            <br></br>
            <strong>Step2:</strong>
            <p>
              Type the TN-PBCCR Visualization Portal address into the browser
              <a
                href="https://tnpbccr.cancerinstitutewia.in/"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://tnpbccr.cancerinstitutewia.in/
              </a>
              <br></br>
            </p>
            <img
              src={ICON_MAP["getting-started-img2"]}
              alt="getting-started-img2"
            />
            <br></br>
            <p>
              The user opens the PBCCR website portal in a supported web
              browser. Upon loading, the homepage is displayed with the
              following primary navigation menus:
            </p>
            <img
              src={ICON_MAP["getting-started-img3"]}
              alt="getting-started-img3"
            />
            <br></br>
            <br></br>
            <h5>Landing Page Accessibility</h5>
            <p>
              The landing page of the portal provides access to several key
              sections through the main navigation menu.
            </p>
            <strong>Home</strong>
            <p>
              a. This is the default landing page of the TN PBCCR Portal. It
              provides an overview of the platform and quick access to important
              sections.
            </p>
            <strong>FactSheet</strong>
            <p>
              This section provides downloadable fact sheets containing
              summarized cancer statistics and related information.
            </p>
            <strong>Data Visualisation</strong>
            <p>
              a. This section contains interactive analytical dashboards,
              including:
            </p>
            <ul className="circle-list">
              <li>Recent Cancer Statistics</li>
              {/* <li>Trend of Cancer Incidence</li>
              <li>Cancer Projection</li> */}
            </ul>
            <strong>Initiative</strong>
            <p>
              This section provides information about the initiative behind the
              portal, including details about the Cancer Institute (WIA).
            </p>
            <strong>Methods</strong>
            <p>
              This page contains information about the statistical methods,
              formulas, and calculation approaches used in the analysis.
            </p>
            <strong>About</strong>
            <p>
              a. This Page provides background information about the Tamil Nadu
              Population-Based Childhood Cancer Registry (TN PBCCR), including
              its objectives, scope, and contribution to cancer surveillance and
              public health planning in Tamil Nadu.
            </p>
            <strong>Help</strong>
            <p>
              a. This section provides contact information and access to the
              User Manual for assistance in using the portal.
            </p>
            <strong>Log In</strong>
            <p>
              a. This option allows registered users to log in to their
              accounts. Account management features and the logout option are
              also available in this menu.
            </p>
            {/* <video src={IMAGES.HELP.GETTING_START} controls width="800" />
             */}
            <a
              href={`${process.env.PUBLIC_URL}/videos/Getting-start.mp4`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Click here to watch Getting Started Video
            </a>
            <br></br>
            <br></br>
          </>
        );
      case "e":
        return (
          <>
            <h5>Account Management</h5>
            <strong>User Registration Process</strong>
            <p>
              Follow the steps below to create a new account on the TN-PBCCR
              Visualization Portal:
            </p>
            <h6>Sign-Up Process</h6>

            <strong>Step 1:</strong>
            <p>
              Open the TN-PBCCR Portal and Click the Login / Sign Up button on
              the homepage
            </p>
            <img src={ICON_MAP["signup-img1"]} alt="signup-img1" />
            <br></br>
            <strong>Step 2:</strong>
            <p>
              You will be redirected to the Login page. Click the Sign Up button
              at the bottom of the login form.
            </p>
            {/* <br></br> */}
            <strong>Step 3:</strong>
            <p>
              A Create Account form is displayed. Enter the following details:
            </p>
            <ul className="circle-list">
              <li>Name</li>
              <li>Email Address</li>
              <li>Phone Number</li>
              <li>Role/Designation</li>
              <li>Hospital/Institution Name</li>
            </ul>
            <img src={ICON_MAP["signup-img2"]} alt="signup-img2" />
            <br></br>
            <strong>Step 4:</strong>
            <p>Click the Sign Up button to submit the registration form.</p>
            <img src={ICON_MAP["signup-img3"]} alt="signup-img3" />
            <br></br>
            <strong>Step 5:</strong>
            <p>
              After successful registration, you will be redirected back to the
              Login Page.
            </p>
            <img src={ICON_MAP["signup-img4"]} alt="signup-img4" />
            <br></br>
            <strong>Step 6:</strong>
            <p>
              If you choose not to continue the process, click the Cancel
              button. This will stop the current process and return you to the
              previous screen without saving any changes.
            </p>
            <img src={ICON_MAP["signup-img5"]} alt="signup-img5" />
            <br></br>
            <p>
              You will be redirected to the login page when after selecting the
              “Cancel” button
            </p>
            <img src={ICON_MAP["signup-img6"]} alt="signup-img6" />
            {/* ---------------- */}

            <br></br>
            {/* <video src={IMAGES.HELP.SIGNUP} controls width="800" /> */}
            <a
              href={`${process.env.PUBLIC_URL}/videos/Sign-up.mp4`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Click here to watch Signup Video
            </a>
            <br></br>
            <br></br>
            <h6>Login Process</h6>
            <p>Follow the steps below to log in to the TN-PBCCR Portal.</p>
            <strong>Step 1:</strong>
            <p>
              After completing the sign-up process, check your registered email
              address to receive your login password.
            </p>
            <ol>
              <li>
                A temporary password will be sent to the registered email
                address.
              </li>
              <img src={ICON_MAP["login-img1"]} alt="login-img1" />
              <br></br>
              <li>
                Open the Login Page of the TN PBCCR Portal and enter the
                following details:
                <ul>
                  <li>Registered Email ID</li>
                  <li>Password received via email</li>
                </ul>
              </li>
              <li>Click the Submit button to proceed.</li>
            </ol>
            <br></br>
            <img src={ICON_MAP["login-img2"]} alt="login-img2" />
            <br></br>
            <strong>Step 2:</strong>
            <p>
              After successful authentication, you will be redirected to the
              Data Visualization Dashboard, where you can access analytical
              features and explore cancer data visualizations.
            </p>
            <img src={ICON_MAP["login-img3"]} alt="login-img3" />
            <br></br>
            <strong>
              Note: Upon first login, users are required to change the temporary
              password before accessing portal features.
            </strong>
            <br></br>
            {/* <video src={IMAGES.HELP.LOGIN} controls width="800" /> */}
            <a
              href={`${process.env.PUBLIC_URL}/videos/Login.mp4`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Click here to watch Login Video
            </a>
            <br></br>
            <br></br>
            {/* ----------- */}
            <h5>Password Management</h5>
            <p>
              This section explains how users can manage their account
              passwords, including changing the existing password and resetting
              a forgotten password.
            </p>
            <h6>Change Password</h6>
            <p>Follow the steps below to change your account password.</p>
            <strong>Step 1:</strong>
            <p>
              Click the Profile icon located at the top corner of the portal
              interface.
            </p>
            <strong>Step 2:</strong>
            <p>From the dropdown menu, select Change Password.</p>
            <img
              src={ICON_MAP["change-password-img1"]}
              alt="change-password-img1"
            />
            <br></br>
            <strong>Step 3:</strong>
            <p>You will be redirected to the Change Password Page</p>
            <strong>Step 4:</strong>
            <p>Enter the following details in the respective fields:</p>
            <ul className="circle-list">
              <li>Old Password</li>
              <li>New Password</li>
              <li>Confirm Password</li>
            </ul>
            <strong>Step 5:</strong>
            <p>Click the Change Password button</p>
            <img
              src={ICON_MAP["change-password-img2"]}
              alt="change-password-img2"
            />
            <br></br>
            <strong>Step 6:</strong>
            <p>
              After updating the password, a confirmation notification will
              appear indicating that the password has been changed successfully.
            </p>
            <img
              src={ICON_MAP["change-password-img3"]}
              alt="change-password-img3"
            />
            <br></br>

            {/* <video src={IMAGES.HELP.CHANGE_PASSWORD} controls width="800" /> */}
            <a
              href={`${process.env.PUBLIC_URL}/videos/Change-password.mp4`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Click here to watch Change Password Video
            </a>
            <br></br>
            <br></br>
            <h6>Forgot Password</h6>
            <p>
              If you forget your password, follow the steps below to reset it.
            </p>
            <strong>Step 1:</strong>
            <p>
              On the Login Page, click the Forgot Password option located on the
              right side of the login form.
            </p>
            <img
              src={ICON_MAP["forgot-password-img1"]}
              alt="forgot-password-img1"
            />
            <br></br>
            <strong>Step 2:</strong>
            <p>
              Enter your registered Email ID and click the Send Password button.
            </p>
            <img
              src={ICON_MAP["forgot-password-img2"]}
              alt="forgot-password-img2"
            />
            <br></br>
            <strong>Step 3:</strong>
            <p>
              Open the email sent to your registered email address and click the
              Reset Password button.
            </p>
            <img
              src={ICON_MAP["forgot-password-img3"]}
              alt="forgot-password-img3"
            />
            <p>You will be redirected to the Reset Password Page</p>
            <strong>Step 4:</strong>
            <p>
              Enter a new password and confirm it to complete the password reset
              process.
            </p>
            <strong>Step 5:</strong>
            <p>
              Click the Change Password button to complete the password reset
              process.
            </p>
            <img
              src={ICON_MAP["forgot-password-img4"]}
              alt="forgot-password-img4"
            />
            <br></br>
            <strong>Step 6:</strong>
            <p>
              After successfully resetting the password, you will be redirected
              to the Login Page.
            </p>
            <img
              src={ICON_MAP["forgot-password-img5"]}
              alt="forgot-password-img5"
            />
            <br></br>
            <br></br>
            <strong>
              Security Notice:The password reset link is valid for a limited
              time period.If the link expires,please initiate a new password
              reset request.
            </strong>
            <br></br>

            {/* <video src={IMAGES.HELP.FORGOT_PASSWORD} controls width="800" /> */}
            <a
              href={`${process.env.PUBLIC_URL}/videos/Forgot-password.mp4`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Click here to watch Forgot Password Video
            </a>
            <br></br>
            <br></br>
            <h6>Profile</h6>
            <h6>Update or Edit Profile</h6>
            <p>
              This section explains how users can view and update their profile
              information in the TN-PBCCR Portal.
            </p>
            <strong>Step 1:</strong>
            <p>
              Click the Profile icon located at the top corner of the portal
              interface.
            </p>
            <strong>Step 2:</strong>
            <p>From the dropdown menu, select Profile.</p>
            <img src={ICON_MAP["profile-img1"]} alt="profile-img1" />
            <strong>Step 3:</strong>
            <p>
              You will be redirected to the Profile Page, where your account
              details are displayed.
            </p>
            <strong>Step 4:</strong>
            <p>
              Update the required information and click the Update Profile
              button to save the changes.
            </p>
            <strong>Step 5:</strong>
            <p>
              After successful submission, a confirmation notification will
              appear stating “Profile Updated Successfully.”
            </p>
            <img src={ICON_MAP["profile-img2"]} alt="profile-img2" />
            <br></br>
            {/* <video src={IMAGES.HELP.PROFILE} controls width="800" /> */}
            <a
              href={`${process.env.PUBLIC_URL}/videos/Profile.mp4`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Click here to watch Profile Video
            </a>
            <br></br>
            <br></br>
            <h6>Logout</h6>
            <p>
              This section explains how users can securely log out from the
              TN-PBCCR Visualization Portal.
            </p>
            <strong>Step 1:</strong>
            <p>
              Click the Profile icon located at the top corner of the portal
              interface
            </p>
            <strong>Step 2:</strong>
            <p>From the dropdown menu, select the Logout option</p>
            <img src={ICON_MAP["logout-img1"]} alt="logout_img1" />
            <br></br>
            <strong>Step 3:</strong>
            <p>
              You will be logged out of the portal and redirected to the Login
              Page.
            </p>
            <img src={ICON_MAP["logout-img1"]} alt="logout_img2" />
            <br></br>
            {/* <video src={IMAGES.HELP.LOGOUT} controls width="800" /> */}
            <a
              href={`${process.env.PUBLIC_URL}/videos/Logout.mp4`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Click here to watch Logout Video
            </a>
            <br></br>
            <br></br>
          </>
        );
      case "f":
        return (
          <>
            <h5>Portal:Step-by-Step Guide</h5>

            <p>
              This section provides a sequential guide for navigating and using
              the core features of the TN-PBCCR Visualization Portal.
            </p>
            <h6>Home Page</h6>
            <p>In the Hero Section have About the childhood cancer</p>
            <img src={ICON_MAP["homepage-img1"]} alt="home_page_img1" />
            <p>
              After the Hero Section you will see the About and Aims TN PBCCR
            </p>
            <img src={ICON_MAP["homepage-img2"]} alt="home_page_img2" />
            <p>After the Aims you will see the Mission, vision, values</p>
            <img src={ICON_MAP["homepage-img3"]} alt="home_page_img3" />
            <p>Last the home page Initiatives and databases </p>
            <img src={ICON_MAP["homepage-img4"]} alt="home_page_img4" />
            {/* <h6>Recent Cancer Statistics</h6>
            <p>
              This section provides access to the most recent cancer statistics
              available in the portal. Users can explore summarized data and
              visual representations of current cancer incidence and related
              indicators.
            </p> */}
            {/* <img src={Homepage_img1} alt="Homepage_img" /> */}
            <br></br>
            {/* <h6>Trend of Cancer Incidence</h6>
            <p>
              This section allows users to analyze historical trends in cancer
              incidence across different time periods and geographic regions.
            </p> */}
            {/* <img src={Homepage_img2} alt="Homepage_img" /> */}
            {/* <br></br> */}
            {/* <h6>Cancer Projection</h6>
            <p>
              This section provides projections of future cancer incidence based
              on statistical models and historical data patterns. These
              projections support planning and policy level decision making.
            </p> */}
            {/* <img src={Homepage_img3} alt="Homepage_img" /> */}
            <br></br>
            {/* <video src={IMAGES.HELP.HOME} controls width="800" /> */}
            <a
              href={`${process.env.PUBLIC_URL}/videos/Home.mp4`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Click here to watch Home Video
            </a>
            <br></br>
            <br></br>
            <h5> Factsheet</h5>
            <p>
              The Fact Sheets section allows users to download summarized cancer
              statistics based on selected filters such as population, district,
              or cancer type.
            </p>
            <p>Follow the steps below to download a fact sheet:</p>
            <strong>Step 1:</strong>
            <p>
              Navigate to the Factsheet section from the navigation bar located
              at the top of the Home Page.
            </p>
            <img src={ICON_MAP["factsheet-img1"]} alt="factsheet_img1" />
            <br></br>
            <strong>Step 2:</strong>
            <p>
              Use the Display dropdown menu to select how you want to filter the
              fact sheet. Available options may include:
            </p>
            <strong>Step 3:</strong>
            <p>The available filtering options include:</p>
            <strong>Display</strong>
            <p>
              Select the type of population data to be displayed. This option
              determines the dataset or view that will be presented in the
              dashboard.
            </p>
            <strong>District</strong>
            <p>
              Choose All Districts to view consolidated data for the entire
              region or select a specific district for localized population
              analysis
            </p>
            <strong>Cancer Type </strong>
            <p>
              Filter the population data based on a selected cancer type. Users
              can choose a specific cancer category for focused analysis.
            </p>
            <strong>Sub-Cancer Type</strong>
            <p>
              Further refine the selected cancer type by choosing a specific
              sub-category, enabling more detailed population-based analysis.
            </p>
            <strong>Step 4:</strong>
            <p>
              An Export/Download button is available in the top-right corner of
              the module.
            </p>
            <p>Users can use this option to:</p>
            <ul>
              <li>Download the filtered dataset for offline analysis. </li>
              <li>
                Export the displayed information for reporting and documentation
                purposes.
              </li>
              <li>
                Save the currently selected view and filter configuration
                results.
              </li>
            </ul>
            <p>
              The exported data will reflect the filters currently applied
              within the Population module
            </p>
            <img src={ICON_MAP["factsheet-img2"]} alt="factsheet_img2" />
            <br></br>
            {/* <video src={IMAGES.HELP.FACTSHEET} controls width="800" /> */}
            <a
              href={`${process.env.PUBLIC_URL}/videos/Factsheet.mp4`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Click here to watch Factsheet Video
            </a>
            <br></br>
            <br></br>
            <h5>Data Visualization</h5>
            <p>
              The Data Visualization section allows users to explore cancer
              statistics through interactive charts, tables, and graphical
              representations. Users can apply filters to customize the dataset
              and view the results in different visual formats.
            </p>
            <p>
              Follow the steps below to use the Data Visualization features.
            </p>
            <strong>Step 1:</strong>
            <p>
              Navigate to the Data Visualization tab located in the navigation
              bar next to the Fact Sheets section.
            </p>
            <img src={ICON_MAP["data-viz-img1"]} alt="data_viz_img1" />
            <br></br>
            <strong>Step 2:</strong>
            <p>
              Click the Data Visualization tab. You will be redirected to the
              Data Visualization Page, where interactive dashboards and
              filtering tools are available.
            </p>
            <p>
              Use the filtering options on the left side of the screen to
              customize and refine the cancer incidence data displayed in the
              dashboard.
            </p>
            <br></br>
            <strong>Step 3:</strong>
            <p>
              On the left side of the screen, use the Available Filtering
              Methods dropdown menus to refine and customize the dataset.
            </p>
            <p>The available filtering options include:</p>
            <strong>Select:</strong>
            <p>
              Choose the category of analysis such as By Place, By Cancer, or
              ASPR.
            </p>
            <strong>Cancer Type:</strong>
            <p>
              Select All Cancer Types Together or choose a specific cancer type
              for detailed analysis.
            </p>
            <strong>Sub Cancer Type:</strong>
            <p>
              Further refine the data by selecting a specific sub-category
              within the chosen cancer type.
            </p>
            <strong>Gender:</strong>
            <p>Filter the data by Both, Male, or Female.</p>
            <strong>Residence District</strong>
            <p>
              Analyze data for All Districts Together or select a specific
              district.
            </p>
            <strong>Age at Diagnosis:</strong>
            <p>
              Specify the age group range for analysis, for example from 0 to 19
              years.
            </p>
            <strong>Year of Diagnosis:</strong>
            <p>Select the required year or range of years for the analysis.</p>
            <strong>Response Type:</strong>
            <p>Choose the type of statistical rate to display:</p>
            <ul className="circle-list">
              <li>
                Incidence – Displays the total number of reported cancer cases
                based on the selected filters
              </li>
              <li>
                Staging – Displays cancer case distribution according to the
                stage of diagnosis, helping analyze the severity and progression
                of cancer cases.
              </li>
            </ul>
            <p>
              After selecting the required filters, the dashboard will
              automatically update and display the corresponding cancer
              incidence or staging statistics and visualizations.
            </p>
            <img src={ICON_MAP["data-viz-img2"]} alt="data_viz_img2" />
            <br></br>
            <strong>Step 4:</strong>
            <p>
              At the top of the main data panel, use the Data Visualization
              Format icons to switch between different visual representations of
              the dataset.
            </p>
            <p>The available visualization formats include:</p>
            <strong>a. Statistical Data Table</strong>
            <p>Displays the dataset in a structured tabular format.</p>
            {/* <img src={Dataviz_img4} alt="Data_Visualization_img" /> */}
            <strong>b. Multi colour Pie Chart</strong>
            <p>
              Displays the proportional distribution of data across selected
              categories.
            </p>
            {/* <img src={Dataviz_img5} alt="Data_Visualization_img" /> */}
            <strong>c. Interactive Treemap</strong>
            <p>
              Displays proportional data distribution using nested rectangles
              based on volume.
            </p>
            {/* <img src={Dataviz_img6} alt="Data_Visualization_img" /> */}
            <strong>d. Horizontal Bar Chart</strong>
            <p>
              Displays comparative values across categories using horizontal
              bars.
            </p>
            {/* <img src={Dataviz_img7} alt="Data_Visualization_img" /> */}
            <strong>e. Trend Line Graph</strong>
            <p>Displays trends and patterns in the data over time.</p>
            <img src={ICON_MAP["data-viz-img3"]} alt="data_viz_img3" />
            <br></br>
            <strong>Step 5:</strong>
            <p>
              A Filter/Expand button is available on the top-right side of the
              table. Users can use this button to:
            </p>
            <ul className="circle-list">
              <li>
                Expand the table view for better visibility and analysis of
                data.
              </li>
              <li>Shrink the table back to its default size when required. </li>
            </ul>
            <p>
              The table layout will automatically adjust based on the selected
              view mode, providing a more flexible and user-friendly experience.
            </p>
            <img src={ICON_MAP["data-viz-img4"]} alt="data_viz_img4" />
            <br></br>
            {/* <video src={IMAGES.HELP.DATA_VISUALIZATION} controls width="800" /> */}
            <a
              href={`${process.env.PUBLIC_URL}/videos/Data-visualization.mp4`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Click here to watch Data Visualization Video
            </a>
            <br></br>
            <br></br>
            {/* <h5>Exporting and Reporting Data </h5>
            <p>
              The TN-PBCCR Visualization Portal empowers authenticated users to
              seamlessly export diverse content formats, tailored to specific
              analytical and reporting requirements.
            </p>
            <h5>Exporting Data Tables</h5>
            <p>
              Users can export structured datasets for further analysis or
              reporting. Data tables can be downloaded in CSV, PDF, or Excel
              formats.
            </p>
            <p>Follow the steps below to export a data table.</p>
            <strong>Step 1:</strong>
            <p>
              Navigate to the required data table within the Data Visualization
              section.
            </p>
            <strong>Step 2:</strong>
            <p>Locate the download icons positioned above the data table.</p>
            <strong>Step 3:</strong>
            <p>
              Click the appropriate icon to download the file in your preferred
              format:
            </p>
            <ul className="circle-list">
              <li>CSV</li>
              <li>PDF</li>
              <li>Excel</li>
            </ul>
            <p>The file will be generated and downloaded to your device.</p>
            <br></br>
            <h5>Exporting Graphical Visualizations</h5>
            <p>
              All graphical visualizations such as charts and treemaps can be
              exported for use in reports, presentations, or further analysis.
              These visualizations can be downloaded in formats such as SVG,
              PNG, or CSV.
            </p>
            <p>Follow the steps below to export a graphical visualization.</p>
            <strong>Step 1:</strong>
            <p>
              Navigate to the required chart or visualization on the Data
              Visualization page.
            </p>
            <strong>Step 2:</strong>
            <p>
              Click the menu icon represented by three horizontal lines located
              at the top right corner of the chart.
            </p>
            <strong>Step 3:</strong>
            <p>A list of download options will appear.</p>
            <strong>Step 4:</strong>
            <p>Select the required format such as:</p>
            <ul className="circle-list">
              <li>SVG</li>
              <li>PNG</li>
              <li>CSV</li>
            </ul>
            <strong>Step 5:</strong>
            <p>
              The selected chart will be downloaded to your device in the chosen
              format.
            </p>
            <br></br> */}
            <h5>Global Footer</h5>
            <p>
              At the bottom of every portal page, a global footer is provided to
              offer quick access to important information, navigation links,
              support resources, and social media channels.
            </p>
            <p>The footer includes the following components.</p>
            <strong>About PBCCR</strong>
            <p>
              This section provides a brief overview of the Population-Based
              Cancer Registry (PBCCR), highlighting its role in collecting,
              maintaining, and analyzing cancer incidence data to support cancer
              surveillance, research, and public health planning.
            </p>
            <strong>Quick Links</strong>
            <p>
              Provides easy access to frequently used portal pages, including:
            </p>
            <ul className="circle-list">
              <li>Home</li>
              <li>Factsheets</li>
              <li>Data Visualization</li>
              <li>Methods</li>
              <li>Initiatives</li>
            </ul>
            <strong>Resources</strong>
            <p>
              Offers access to important portal information and support pages,
              including:
            </p>
            <ul className="circle-list">
              <li>About</li>
              <li>Help</li>
              <li>Contact Us</li>
              <li>Terms of Use</li>
              <li>Privacy Policy</li>
            </ul>
            <strong>Social Links</strong>
            <p>
              Provides links to official social media platforms and
              communication channels, enabling users to stay informed about
              updates, announcements, and related activities.
            </p>
            <strong>Powered By</strong>
            <p>
              Displays information about the organization or technology partner
              responsible for developing and maintaining the portal.
            </p>
            <strong>Terms of Use and Privacy Policy</strong>
            <p>
              Links to Terms of Use and Privacy Policy are available in the
              footer section. Users are expected to comply with all applicable
              policies while accessing and using the portal.
            </p>
            <p>These policies Outline</p>
            <ul className="circle-list">
              <li>Appropriate use of portal data and resources</li>
              <li> Data privacy and confidentiality requirements</li>
              <li>User responsibilities and acceptable usage guidelines</li>
              <li>Protection of institutional and research information </li>
              <li>Compliance with applicable data governance standards </li>
            </ul>
            <p>
              By using the portal, users acknowledge and agree to abide by these
              terms and policies.
            </p>
            <img src={ICON_MAP["footer-img1"]} alt="footer_img1" />
            <br></br>
            {/* <video src={IMAGES.HELP.GLOBAL_FOOTER} controls width="800" /> */}
            <a
              href={`${process.env.PUBLIC_URL}/videos/Global-footer.mp4`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Click here to watch Footer Video
            </a>
            <br></br>
            <br></br>
            <h6>About TN-PBCCR</h6>
            <p>
              This section provides background information about the Tamil Nadu
              Cancer Registry Project (TN-PBCCR), including its objectives,
              scope, and contribution to cancer surveillance and public health
              planning in Tamil Nadu.
            </p>
            {/* <br></br> */}
            <h6>Connect With Us</h6>
            <p>
              This section provides contact and support information for users
              who require assistance with the portal. It may include contact
              details for technical support, institutional communication
              channels, or other relevant help resources.
            </p>
          </>
        );
      case "g":
        return (
          <>
            <h5>Data Coverage and Definitions</h5>
            <p>
              This section outlines the scope of the data available in the
              TN-PBCCR Visualization Portal, including the time periods covered
              and key definitions used in the analysis of cancer statistics.
            </p>
            <h5>Coverage Timeframes</h5>
            <p>
              The portal contains both historical cancer data and projected
              estimates to support epidemiological analysis and future planning.
            </p>
            <h6>Historical Data (Years Covered:2022)</h6>
            <p>
              This dataset includes actual recorded cancer cases obtained from
              registry records. The data contains verified incidence records
              along with demographic classifications such as age group, gender,
              and district of residence.
            </p>
            {/* <h6>Projection Data (Years Covered: 2022 to 2028)</h6>
            <p>
              These datasets represent statistical projections generated using
              historical trends and analytical models to estimate future cancer
              incidence patterns.
            </p> */}
            <h6>Additional Classifications</h6>
            <p>
              All datasets include the following classifications to support
              detailed analysis:
            </p>
            <ul className="circle-list">
              <li>District wise coverage</li>
              <li>Gender distribution</li>
              <li>Age group categorization</li>
            </ul>
            <p>
              These classifications enable comprehensive epidemiological
              analysis across different population segments.
            </p>
            <h6>Definitions</h6>
            <p>
              The following definitions are used throughout the portal and
              related analytical outputs.
            </p>
            <strong>Cancer Case Definition</strong>
            <p>
              A confirmed diagnosis of cancer based on medical records and
              pathological reports.All neoplasms with behavior code “3”
              according to the International Classification of Diseases for
              Oncology (ICD O Third Edition) are considered reportable and are
              included in the cancer registry.
            </p>
            <strong>Age Group Classification</strong>
            <p>
              Age groups follow the World Health Organization standard five year
              intervals, such as:
              <strong>
                Childhood cancer cases are defined as individuals aged 0 to 19
                years.
              </strong>
            </p>
            <strong>Incidence Definition</strong>
            <p>
              Cancer incidence refers to the number of new cancer cases
              diagnosed within a defined population during a specific time
              period and geographic area.
            </p>
            <p>
              All cancer incidence rates in the portal are expressed per 100,000
              population, following international epidemiological standards.
            </p>
          </>
        );
      case "h":
        return (
          <>
            <h5>Troubleshooting</h5>
            <p>
              While using the TN-PBCCR Visualization Portal, users may
              occasionally encounter minor technical issues related to data
              display, loading speed, or filter selections. The following
              troubleshooting steps can help resolve the most common problems
              before contacting technical support.
            </p>

            <h6>Charts Not Loading Properly</h6>

            <strong>Issue</strong>
            <p>
              Charts or visualizations fail to load or appear blank on the
              screen.
            </p>

            <strong>Solution</strong>
            <p>
              Clear your browser cache and refresh the page. Using the latest
              version of a supported browser such as Google Chrome, Microsoft
              Edge, or Mozilla Firefox can also help resolve most display
              related issues.
            </p>

            <h6>Data Not Refreshing</h6>
            <strong>Issue</strong>
            <p>
              Updated results do not appear after applying new filters or
              changing selections.
            </p>
            <strong>Solution</strong>
            <p>
              Reset all filters to their default settings and then reapply the
              required filters. Ensure that all filter fields have valid
              selections before generating the visualization.
            </p>

            <h6>Incorrect Numbers Displayed</h6>
            <strong>Issue</strong>
            <p>
              The displayed statistics appear inconsistent with the expected
              results.
            </p>
            <strong>Solution</strong>
            <p>
              Verify that the correct category, indicator, time period, and
              geographic selection have been applied in the filtering options.
            </p>

            <h6>Slow Loading Times</h6>
            <strong>Issue</strong>
            <p>
              The portal takes longer than expected to load charts, tables, or
              visualizations.
            </p>
            <strong>Solution</strong>
            <p>
              Ensure that your internet connection is stable. Close unnecessary
              browser tabs or background applications to improve system
              performance. A system with sufficient memory and a supported
              browser will ensure smoother operation.
            </p>
          </>
        );
      case "i":
        return (
          <>
            <h5>Data Governance</h5>
            <p>
              Users accessing the TN-PBCCR Visualization Portal are expected to
              adhere to the following data governance principles and usage
              guidelines:
            </p>
            <ol className="numbered-list">
              <li>
                Appropriate acknowledgement and citation of TN-PBCCR shall be
                provided when using data, visualizations, reports, or analytical
                outputs derived from the portal in publications, presentations,
                research work, or public communications.
              </li>
              <li>
                Users shall not reproduce, redistribute, share, or publish raw
                data obtained from the portal without prior authorization from
                the concerned authority.
              </li>
              <li>
                Users shall not reproduce, redistribute, share, or publish raw
                data obtained from the portal without prior authorization from
                the concerned authority.
              </li>
              <li>
                Statistical estimates, projections, and analytical outputs
                available through the portal should be interpreted responsibly
                and within the appropriate epidemiological and analytical
                context
              </li>
              <li>
                Users are expected to comply with all applicable institutional
                policies, ethical standards, data governance regulations, and
                responsible data usage practices while accessing and utilizing
                information from the portal.
              </li>
              <li>
                The portal and its contents are intended solely for academic,
                scientific, public health, research, and policy-support
                purposes.
              </li>
            </ol>
          </>
        );
      case "j":
        return (
          <>
            <h5>Conclusion</h5>
            <p>
              The TN-PBCCR Visualization Portal provides an interactive and
              structured platform for analyzing childhood cancer incidence
              statistics across Tamil Nadu. The portal enables users to explore
              epidemiological data through visualizations, statistical tables,
              and analytical tools designed to support research, surveillance,
              and public health monitoring activities.
            </p>
            <p>
              The system facilitates data-driven decision-making and supports
              district-level epidemiological assessment, enabling policymakers,
              researchers, clinicians, and healthcare institutions to better
              understand childhood cancer trends, distribution patterns, and
              regional variations.
            </p>
            <p>
              This user manual should be reviewed and updated periodically under
              the following circumstances:
            </p>
            <ul className="circle-list">
              <li>Publication of new childhood cancer registry data</li>
              <li>
                Revision of statistical methods, projection models, or
                analytical methodologies
              </li>
              <li>
                Enhancement of portal features, user interface, or analytical
                capabilities
              </li>
            </ul>
            <p>
              Regular updates to this manual will ensure that users continue to
              have accurate guidance for effectively using the TNCrest
              Visualization Portal.
            </p>
          </>
        );
      case "k":
        return (
          <>
            <h5>Appendices</h5>
            <h6>Appendix A: Abbreviations and Acronyms</h6>
            <p>
              This section lists commonly used abbreviations and acronyms used
              in the TN-PBCCR Visualization Portal.
            </p>
            <ul className="circle-list">
              <li>ASpR - Age Specific Rate</li>
              <li>ASR - Age Standardized Rate</li>
              <li>CIR - Crude Incidence Rate</li>
              <li>CR - Cumulative Risk</li>
              <li>GCC - Greater Chennai Corporation</li>
              <li>
                ICC3 - International Classification of Childhood Cancer, Version
                3
              </li>
              <li>PBCCR - Population-Based Childhood Cancer Registry</li>
              <li>TNCRP - Tamil Nadu Cancer Registry Project</li>
              <li>
                TNPBCCR - Tamil Nadu Population-Based Childhood Cancer Registry
              </li>
            </ul>
            <strong>Copyright</strong>
            <p>© 2026 All rights reserved by Evolve Bizcon Pvt Ltd.</p>
            <strong>Note</strong>
            <p>This manual should be updated whenever:</p>
            <ul className="circle-list">
              <li>New registry data is published</li>
              <li>Projection models are revised</li>
              <li>Portal features are enhanced</li>
            </ul>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="help-page">
      <section className="help-hero">
        <div className="help-hero-media">
          <img src={IMAGES.ICONS.HELP_SECTION} alt="Healthcare support" />
        </div>
        <div className="help-hero-copy">
          <h1>Help &amp; Support</h1>
          <p className="help-hero-subtitle">
            We&rsquo;re here to help you understand and use PBCCR effectively
          </p>
          <p>
            Welcome to the PBCCR Help Center. This platform is designed to
            provide cancer data insights and support awareness. If you have any
            questions about using the system, accessing data, or understanding
            reports, you can find guidance here.
          </p>
        </div>
      </section>
      <div className="row mt-1">
        {/* Left Side Tabs */}
        <div className="col-md-3">
          <div className="list-group p-1">
            <button
              className={`list-group-item list-group-item-action  ${
                activeTab === "a" ? "active" : ""
              }`}
              onClick={() => setActiveTab("a")}
            >
              Introduction and Overview
            </button>

            <button
              className={`list-group-item list-group-item-action ${
                activeTab === "b" ? "active" : ""
              }`}
              onClick={() => setActiveTab("b")}
            >
              System Requirements
            </button>

            <button
              className={`list-group-item list-group-item-action ${
                activeTab === "c" ? "active" : ""
              }`}
              onClick={() => setActiveTab("c")}
            >
              User Access Levels
            </button>

            <button
              className={`list-group-item list-group-item-action ${
                activeTab === "d" ? "active" : ""
              }`}
              onClick={() => setActiveTab("d")}
            >
              Getting Started
            </button>

            <button
              className={`list-group-item list-group-item-action ${
                activeTab === "e" ? "active" : ""
              }`}
              onClick={() => setActiveTab("e")}
            >
              Account Management
            </button>

            <button
              className={`list-group-item list-group-item-action ${
                activeTab === "f" ? "active" : ""
              }`}
              onClick={() => setActiveTab("f")}
            >
              Portal Step-by-Step Guide
            </button>

            <button
              className={`list-group-item list-group-item-action ${
                activeTab === "g" ? "active" : ""
              }`}
              onClick={() => setActiveTab("g")}
            >
              Data Coverage and Definitions
            </button>

            <button
              className={`list-group-item list-group-item-action ${
                activeTab === "h" ? "active" : ""
              }`}
              onClick={() => setActiveTab("h")}
            >
              Troubleshooting
            </button>

            <button
              className={`list-group-item list-group-item-action ${
                activeTab === "i" ? "active" : ""
              }`}
              onClick={() => setActiveTab("i")}
            >
              Data Governance
            </button>

            <button
              className={`list-group-item list-group-item-action ${
                activeTab === "j" ? "active" : ""
              }`}
              onClick={() => setActiveTab("j")}
            >
              Conclusion
            </button>

            <button
              className={`list-group-item list-group-item-action ${
                activeTab === "k" ? "active" : ""
              }`}
              onClick={() => setActiveTab("k")}
            >
              Appendices
            </button>
          </div>
        </div>

        {/* Right Side Content */}
        <div
          ref={contentRef}
          className="col-md-9 border mt-1 pt-1 p-3 tab-content-area"
          style={{ maxHeight: "77vh", overflowY: "auto" }}
        >
          {renderContent()}
        </div>
      </div>

      <section
        className="help-faq-section"
        aria-label="Frequently asked questions"
      >
        <div className="help-faq-heading">
          <h2>Frequently Asked Questions</h2>
          <p>Find quick answers to common inquiries about our platform</p>
        </div>

        <div className="help-faq-list">
          {faqs.map((faq) => {
            const isOpen = activeFaq === faq.question;
            return (
              <article
                className={`help-faq-item${isOpen ? " is-open" : ""}`}
                key={faq.question}
              >
                <button
                  type="button"
                  className="help-faq-trigger"
                  onClick={() => toggleFaq(faq.question)}
                  aria-expanded={isOpen}
                  aria-controls={`${faq.question.replace(/\W+/g, "-")}-answer`}
                >
                  <span>{faq.question}</span>
                  <span className="help-faq-chevron" aria-hidden>
                    {isOpen
                      ? React.createElement(FaChevronUp as any, {
                          "aria-hidden": true,
                        })
                      : React.createElement(FaChevronDown as any, {
                          "aria-hidden": true,
                        })}
                  </span>
                </button>
                {isOpen && (
                  <div
                    className="help-faq-answer"
                    id={`${faq.question.replace(/\W+/g, "-")}-answer`}
                  >
                    <p>{faq.answer}</p>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>

      <section className="help-contact-section" aria-label="Contact PBCCR">
        <h2>Contact Us</h2>
        <div className="help-contact-grid">
          <div>
            <strong>Population-Based Childhood Cancer Registry</strong>
            <p>
              Cancer Institute (WIA), Dr. S. Krishnamurthi Campus, 38 Sardar
              Patel Road, adjacent to I.I.T. Madras, Chennai, Tamil Nadu, India
              - 600036.
            </p>
          </div>
          <div>
            <strong>Email</strong>
            <p>
              <a href="mailto:pbccr@cancerinstitutewia.org">
                pbccr@cancerinstitutewia.org
              </a>
            </p>
          </div>
          <div>
            <strong>Website</strong>
            <p>
              <a
                href="http://www.cancerinstitutewia.in"
                target="_blank"
                rel="noreferrer"
              >
                www.cancerinstitutewia.in
              </a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Help;
